import React from 'react';
import type { ElementRef, Ref, ReactNode, ReactElement } from 'react';

import { styled } from '@backyard-ui/styles';
import type { StitchesCSS, CSSProperties } from '@backyard-ui/styles';

import { Col } from './Col';
import styles from './Grid.styles';

import { filterFalsyValue } from '../../utils';

export interface GridProps {
  /**
   * The CSS `align-items` property
   */
  align?: CSSProperties['alignItems'];

  /**
   * The amount of columns in each row
   */
  columns?: number;

  /**
   * The space between each column
   */
  gutter?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * The CSS `justify-content` property
   */
  justify?: CSSProperties['justifyContent'];

  /**
   * If true remove the space between columns
   */
  noGutters?: boolean;

  /**
   * If true the component will occupy remaining columns
   * by setting 'flex-grow: 1'
   */
  isGrow?: boolean;

  /**
   * The CSS `flex-direction` property
   */
  isReverse?: boolean;

  /**
   * Stitches `css` func
   */
  css?: StitchesCSS;

  /**
   * The ref to the HTML DOM element
   */
  ref?: Ref<HTMLDivElement>;

  /**
   * The children elements
   */
  children: ReactNode;
}

const GridEl = styled('div', styles);

const GridBase = React.forwardRef<ElementRef<typeof GridEl>, GridProps>(
  (props, ref) => {
    const {
      children,
      columns = 12,
      justify,
      align,
      noGutters,
      isGrow,
      ...rest
    } = props;

    const styles = filterFalsyValue({
      alignItems: align,
      justifyContent: justify,
    });

    const cols = (React.Children.toArray(children) as ReactElement[]).map(
      (child, index) =>
        React.cloneElement(child, {
          key: index,
          columns,
          isGrow,
          noGutters,
        })
    );

    return (
      <GridEl ref={ref} css={{ ...styles }} noGutters={noGutters} {...rest}>
        {cols}
      </GridEl>
    );
  }
);

const Grid = Object.assign({}, GridBase, {
  Col,
});

Grid.displayName = 'Grid';

export default Grid;
